import { ArrowLeft, Columns2, PanelTop, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, IconButton, Tooltip, cn } from '@/components/ui';
import { RightPanelContext, type RightPanelConfig, type RightPanelItem } from './rightPanelContext';
import type { PaneId } from './types';
import { useWorkspace } from './workspaceContext';
import { useScrollMemory } from './useScrollMemory';

function load(key: string): RightPanelItem | null {
  try {
    const item: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (!item || typeof item !== 'object') return null;
    const r = item as Partial<RightPanelItem>;
    if (typeof r.id !== 'string' || typeof r.title !== 'string' || typeof r.path !== 'string') return null;
    if (!r.path.startsWith('/') || r.id.length > 200 || r.title.length > 300 || r.path.length > 2000)
      return null;
    return {
      id: r.id,
      title: r.title,
      path: r.path,
      eyebrow: typeof r.eyebrow === 'string' ? r.eyebrow : undefined,
    };
  } catch {
    return null;
  }
}

/** Включается только новым свойством WorkspaceCanvas.rightPanel; прежние вкладки не оборачивает. */
export function RightPanelFrame({
  config,
  tabId,
  path,
  pane,
  hidden,
  compactHeader = false,
  children,
}: {
  config: RightPanelConfig;
  tabId: string;
  path: string;
  pane: PaneId;
  hidden: boolean;
  /** Значки действий в строке заголовка; прежняя шапка остаётся умолчанием. */
  compactHeader?: boolean;
  children: ReactNode;
}) {
  const ws = useWorkspace();
  const key = `${config.storageKey}:${tabId}:${path}`;
  const [item, setItem] = useState(() => load(key));
  const [overlay, setOverlay] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useScrollMemory(contentRef, key + ':scroll');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const focusRequested = useRef(false);
  const titleId = useId();
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setOverlay(width > 0 && width < 944);
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(item));
    } catch {
      /* Хранилище может быть недоступно. */
    }
  }, [item, key]);

  const close = useCallback(
    (restore = true) => {
      const old = item;
      try {
        localStorage.setItem(key, 'null');
      } catch {
        /* Вкладка может сразу переехать в другое окно. */
      }
      setItem(null);
      if (!restore) return;
      requestAnimationFrame(() => {
        const root = rootRef.current;
        const saved = triggerRef.current;
        const trigger =
          saved?.isConnected && root?.contains(saved)
            ? saved
            : root?.querySelector<HTMLElement>(`[data-panel-trigger="${CSS.escape(old?.id ?? '')}"]`);
        (trigger ?? root?.querySelector<HTMLElement>('[data-panel-default-focus]'))?.focus({
          preventScroll: true,
        });
      });
    },
    [item, key],
  );

  const open = useCallback((next: RightPanelItem, trigger?: HTMLElement) => {
    triggerRef.current =
      trigger ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    focusRequested.current = true;
    setItem({ ...next });
  }, []);

  useLayoutEffect(() => {
    if (!hidden && item && focusRequested.current) {
      titleRef.current?.focus({ preventScroll: true });
      focusRequested.current = false;
    }
  }, [hidden, item]);

  const api = useMemo(() => ({ item, open, close: () => close() }), [item, open, close]);
  useEffect(() => {
    if (!item || hidden || ws.state.focused !== pane) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      close();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [close, hidden, item, pane, ws.state.focused]);

  const openInTab = () => {
    if (!item) return;
    const existing = Object.values(ws.state.tabs).find((tab) => tab.path === item.path);
    const target = existing ? (ws.state.side?.tabs.includes(existing.id) ? 'side' : 'main') : pane;
    close(false);
    ws.go(item.path, { pane });
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-panel-frame="${target}"]`)
        ?.closest('section')
        ?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
        ?.focus();
    });
  };

  const openBeside = () => {
    if (!item) return;
    close(false);
    ws.openBeside(item.path, pane);
    const other = pane === 'main' ? 'side' : 'main';
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-panel-frame="${other}"] [data-panel-default-focus]`)
        ?.focus();
    });
  };

  return (
    <RightPanelContext.Provider value={api}>
      <div
        ref={rootRef}
        data-panel-frame={pane}
        className="@container/pane relative flex size-full min-w-0 overflow-hidden"
        onFocusCapture={() => ws.focus(pane)}
      >
        <div
          ref={contentRef}
          data-scrollport=""
          data-panel-content=""
          inert={Boolean(item && overlay)}
          className={cn(
            'h-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto',
            item && 'hidden @detail-full/pane:block @detail-wide/pane:mr-96',
          )}
        >
          {children}
        </div>
        {item && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Закрыть панель свойств"
            onClick={() => close()}
            className="absolute inset-0 z-raised bg-overlay @detail-wide/pane:hidden"
          />
        )}
        {item && (
          <aside
            aria-labelledby={titleId}
            data-slot="right-panel"
            data-shell-card="detail"
            className="absolute inset-y-0 right-0 z-drawer flex w-full animate-slide-in-right flex-col border-l border-line bg-card shadow-lg motion-reduce:animate-none @detail-full/pane:w-96 @detail-wide/pane:shadow-none"
          >
            {compactHeader ? (
              <header data-panel-header="compact" className="shrink-0 border-b border-line p-3">
                <div className="flex min-w-0 items-start gap-2">
                  <div className="min-w-0 flex-1">
                    {item.eyebrow && (
                      <p className="mb-1 truncate type-overline text-fg-subtle">{item.eyebrow}</p>
                    )}
                    <h2
                      ref={titleRef}
                      id={titleId}
                      tabIndex={-1}
                      title={config.title?.(item) ?? item.title}
                      className="truncate type-subheading text-fg focus-visible:outline-none"
                    >
                      {config.title?.(item) ?? item.title}
                    </h2>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Tooltip content="Открыть во вкладке" side="bottom">
                      <IconButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Открыть во вкладке"
                        onClick={openInTab}
                      >
                        <PanelTop />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      content={pane === 'main' ? 'Открыть во втором окне' : 'Открыть в основном окне'}
                      side="bottom"
                    >
                      <IconButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={pane === 'main' ? 'Открыть во втором окне' : 'Открыть в основном окне'}
                        onClick={openBeside}
                      >
                        <Columns2 />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Закрыть панель" side="bottom">
                      <IconButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Закрыть панель"
                        onClick={() => close()}
                      >
                        <X />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </header>
            ) : (
              <header className="shrink-0 border-b border-line p-4">
                <div className="flex items-start gap-3">
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Назад к содержимому"
                    onClick={() => close()}
                    className="@detail-full/pane:hidden"
                  >
                    <ArrowLeft />
                  </IconButton>
                  <div className="min-w-0 flex-1">
                    {item.eyebrow && <p className="mb-1 type-overline text-fg-subtle">{item.eyebrow}</p>}
                    <h2
                      ref={titleRef}
                      id={titleId}
                      tabIndex={-1}
                      className="type-subheading text-fg focus-visible:outline-none"
                    >
                      {config.title?.(item) ?? item.title}
                    </h2>
                  </div>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Закрыть панель"
                    onClick={() => close()}
                    className="hidden @detail-full/pane:inline-flex"
                  >
                    <X />
                  </IconButton>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <Button variant="secondary" size="sm" className="w-full" onClick={openInTab}>
                    <PanelTop />
                    Открыть во вкладке
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full" onClick={openBeside}>
                    <Columns2 />
                    {pane === 'main' ? 'Открыть во втором окне' : 'Открыть в основном окне'}
                  </Button>
                </div>
              </header>
            )}
            <div data-scrollport="" className="min-h-0 flex-1 overflow-y-auto p-5">
              {config.render(item)}
            </div>
          </aside>
        )}
      </div>
    </RightPanelContext.Provider>
  );
}
